/**
 * Marketing Order Form API.
 *
 * Team members request printed and branded materials (shirts, banners, flyers,
 * stickers). Submitting creates a task tagged 'marketing' and emails everyone
 * holding the marketing role; logo files are uploaded to that task as
 * attachments. Fields mirror the TechForce Robotics Team Marketing Order Form.
 */

import { API_BASE } from '../config/apiBase';

export type MarketingProofApproval = 'Proof requested' | 'No proof needed';

export interface MarketingOrderRow {
  id: number;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  company_name: string;
  items_requested: string;
  quantity_needed: string;
  turnaround_time: string;
  shipping_address: string;
  shipping_time_preference: string;
  preferred_colors: string;
  text_messaging: string;
  design_notes: string;
  proof_approval: MarketingProofApproval;
  payment_method: string;
  billing_address: string;
  special_requests: string;
  /** Task created for the marketing team to fulfil this order. */
  task_id: number | null;
  task_name: string | null;
  task_status: string | null;
  submitted_by_user_id: number | null;
  submitted_by_username: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Editable body of the form. Everything is free text, as on the paper form. */
export interface MarketingOrderDraft {
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  company_name: string;
  items_requested: string;
  quantity_needed: string;
  turnaround_time: string;
  shipping_address: string;
  shipping_time_preference: string;
  preferred_colors: string;
  text_messaging: string;
  design_notes: string;
  proof_approval: MarketingProofApproval;
  payment_method: string;
  billing_address: string;
  special_requests: string;
}

export function emptyMarketingOrderDraft(): MarketingOrderDraft {
  return {
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    company_name: '',
    items_requested: '',
    quantity_needed: '',
    turnaround_time: '',
    shipping_address: '',
    shipping_time_preference: '',
    preferred_colors: '',
    text_messaging: '',
    design_notes: '',
    proof_approval: 'Proof requested',
    payment_method: '',
    billing_address: '',
    special_requests: '',
  };
}

async function jsonOrThrow<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || fallback);
  return data as T;
}

/** Marketing and admin get every order; everyone else gets their own. */
export async function fetchMarketingOrders(userId: number): Promise<MarketingOrderRow[]> {
  const res = await fetch(`${API_BASE}/api/marketing/orders?userId=${encodeURIComponent(userId)}`);
  const data = await jsonOrThrow<{ orders: MarketingOrderRow[] }>(
    res,
    'Failed to fetch marketing orders'
  );
  return data.orders ?? [];
}

export async function submitMarketingOrder(
  userId: number,
  draft: MarketingOrderDraft,
  /** Names of files about to be uploaded, so the email can list them. */
  attachmentNames: string[] = []
): Promise<MarketingOrderRow> {
  const res = await fetch(`${API_BASE}/api/marketing/orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...draft, user_id: userId, attachment_names: attachmentNames }),
  });
  return jsonOrThrow(res, 'Failed to submit marketing order');
}
